import { useEffect, useId, useRef, type ReactNode, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Controls.tsx';

function isBackdrop(event: PointerEvent<HTMLDialogElement>) {
    if (event.target !== event.currentTarget) return false;
    const bounds = event.currentTarget.getBoundingClientRect();
    return (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
    );
}

/** Native modal dialog provides focus containment, Escape and focus restoration. */
export function Modal({
    opened,
    onClose,
    title,
    children,
}: {
    opened: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const dialog = useRef<HTMLDialogElement>(null);
    const backdropPointer = useRef<number | null>(null);
    useEffect(() => {
        const element = dialog.current;
        if (!opened || !element) return;
        const previousOverflow = document.body.style.overflow;
        backdropPointer.current = null;
        element.showModal();
        document.body.style.overflow = 'hidden';
        return () => {
            element.close();
            document.body.style.overflow = previousOverflow;
        };
    }, [opened]);
    return (
        <dialog
            ref={dialog}
            aria-labelledby={titleId}
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            onPointerDown={(event) => {
                backdropPointer.current =
                    event.isPrimary && event.button === 0 && isBackdrop(event) ? event.pointerId : null;
            }}
            onPointerUp={(event) => {
                const dismiss =
                    event.isPrimary &&
                    event.button === 0 &&
                    backdropPointer.current === event.pointerId &&
                    isBackdrop(event);
                backdropPointer.current = null;
                if (dismiss) onClose();
            }}
            onPointerCancel={() => {
                backdropPointer.current = null;
            }}
            className="fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-xl border border-line bg-surface p-6 text-ink shadow-xl backdrop:bg-overlay"
        >
            {opened && (
                <>
                    <div className="mb-5 flex items-start justify-between gap-4">
                        <h2 id={titleId} className="text-xl font-bold">
                            {title}
                        </h2>
                        <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('platform.ui.close')}>
                            <svg
                                aria-hidden="true"
                                viewBox="0 0 24 24"
                                className="size-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path d="m6 6 12 12M6 18 18 6" />
                            </svg>
                        </Button>
                    </div>
                    {children}
                </>
            )}
        </dialog>
    );
}
